// Mock reference module (spec §2): banks, branches, IFSC, countries,
// currencies, recharge/bill lists, locate-us and legal documents.
import type { BankRef, BranchLocation, BranchRef, CountryRef } from '@/types/phase2.types';
import {
  BANKS,
  BILLERS,
  BILL_CATEGORIES,
  CIRCLES,
  COUNTRIES,
  LEGAL_DOCUMENTS,
  LOCATE_US,
  OPERATORS,
} from '../data';
import { pause } from '../util';

export const getBanks = async (): Promise<BankRef[]> => {
  await pause(150);
  return BANKS.map(({ code, name, ifscPrefix }) => ({ code, name, ifscPrefix }));
};

export const getBranches = async (bankCode: string): Promise<BranchRef[]> => {
  await pause(150);
  const bank = BANKS.find((b) => b.code === bankCode);
  if (!bank) return [];
  return bank.branches.map(([name, city], i) => ({
    name,
    city,
    ifsc: `${bank.ifscPrefix}000000${i + 1}`,
  }));
};

export const findBranchByIfsc = async (
  ifsc: string
): Promise<{ bank: BankRef; branch: BranchRef } | null> => {
  await pause(150);
  const upper = ifsc.trim().toUpperCase();
  for (const bank of BANKS) {
    const idx = bank.branches.findIndex(
      (_b, i) => `${bank.ifscPrefix}000000${i + 1}` === upper
    );
    if (idx >= 0) {
      const [name, city] = bank.branches[idx];
      return {
        bank: { code: bank.code, name: bank.name, ifscPrefix: bank.ifscPrefix },
        branch: { name, city, ifsc: upper },
      };
    }
  }
  return null;
};

export const getCountries = async (): Promise<CountryRef[]> => {
  await pause(150);
  return COUNTRIES.map((c) => ({ ...c }));
};

export const getOperators = async (): Promise<string[]> => {
  await pause(100);
  return [...OPERATORS];
};

export const getCircles = async (): Promise<string[]> => {
  await pause(100);
  return [...CIRCLES];
};

export const getBillCategories = async (): Promise<string[]> => {
  await pause(100);
  return [...BILL_CATEGORIES];
};

export const getBillers = async (category: string): Promise<string[]> => {
  await pause(150);
  return [...(BILLERS[category] ?? [])];
};

export const getLocations = async (): Promise<BranchLocation[]> => {
  await pause(150);
  return LOCATE_US.map((l) => ({ ...l }));
};

export const getLegalDocument = async (type: string): Promise<string> => {
  await pause(150);
  return LEGAL_DOCUMENTS[type] ?? 'Document unavailable.';
};
